// ./services/pdfSectionsService.js
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import moment from "moment";

/**
 * Exporta PDF para una sección:
 *  - section: "marcaciones" | "asistencias" | "extras"
 *  - rows: array de datos ya filtrados (lo que querés exportar)
 *  - meta: info de encabezado
 */
export function exportLiquidacionSectionPDF({ section, rows = [], meta = {} }) {
  const doc = new jsPDF("p", "mm", "a4");
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();

  const {
    pacienteNombre = "",
    idLiquidacion = "",
    tabName = "",
    fechainicio = "",
    fechafin = "",
    estado = "",
  } = meta;

  const COLORS = {
    blue: [44, 123, 184],      // similar al azul del ejemplo
    lightRow: [245, 245, 245], // zebra
    border: [220, 220, 220],
    grayText: [120, 120, 120],
    black: [0, 0, 0],
  };

  // ===== Helpers =====
  const fmtMoney = (n) => toNumber(n).toLocaleString("es");
  const fmtHours = (n) => Number(toNumber(n)).toFixed(2);

  const titleMap = {
    marcaciones: "Marcaciones",
    asistencias: "Asistencias",
    extras: "Otros Servicios",
  };
  const sectionTitle = titleMap[section] || "Detalle";

  // ===== Header (como imagen: nombre centrado + Liquidación) =====
  let y = 18;

  doc.setFont("helvetica", "bold");
  doc.setFontSize(20);
  doc.setTextColor(...COLORS.black);
  doc.text(safe(pacienteNombre) || "—", pageWidth / 2, y, { align: "center" });

  y += 8;
  doc.setFont("helvetica", "normal");
  doc.setFontSize(12);
  doc.setTextColor(...COLORS.grayText);
  doc.text("Liquidación", pageWidth / 2, y, { align: "center" });

  // línea separadora suave
  y += 6;
  doc.setDrawColor(...COLORS.border);
  doc.setLineWidth(0.3);
  doc.line(14, y, pageWidth - 14, y);

  // ===== Meta (chiquito y prolijo) =====
  y += 7;
  doc.setFontSize(9);
  doc.setTextColor(...COLORS.black);

  const periodo = `${fechainicio || "-"} a ${fechafin || "-"}`;



  // derecha
  doc.setTextColor(...COLORS.grayText);
  doc.text(`Periodo: ${periodo}`, pageWidth - 14, y, { align: "right" });

  // ===== Título de sección centrado =====
  y += 16;
  doc.setFont("helvetica", "bold");
  doc.setFontSize(11);
  doc.setTextColor(...COLORS.black);
  doc.text(sectionTitle, pageWidth / 2, y, { align: "center" });

  // ===== Table data =====
  const build = buildTable(section, rows);

  // ===== Table style (como imagen: header azul + zebra + bordes suaves) =====
  autoTable(doc, {
    startY: y + 4,
    head: [build.headers],
    body: build.body,
    foot: build.foot ? [build.foot] : undefined,

    theme: "grid",
    margin: { left: 14, right: 14 },

    styles: {
      font: "helvetica",
      fontSize: 9,
      cellPadding: { top: 2.2, right: 2.2, bottom: 2.2, left: 2.2 },
      lineColor: COLORS.border,
      lineWidth: 0.2,
      textColor: COLORS.black,
      valign: "middle",
    },

    headStyles: {
      fillColor: COLORS.blue,
      textColor: 255,
      fontStyle: "bold",
      lineWidth: 0,
    },

    alternateRowStyles: {
      fillColor: COLORS.lightRow,
    },

    footStyles: {
      fillColor: 255,
      textColor: COLORS.black,
      fontStyle: "bold",
      lineColor: COLORS.border,
      lineWidth: 0.2,
    },

    columnStyles: build.columnStyles || {},

    didDrawPage: () => {
      // Footer como el tuyo
      const footerY = pageHeight - 8;
      doc.setFont("helvetica", "normal");
      doc.setFontSize(8);
      doc.setTextColor(...COLORS.grayText);

      const footer = `Generado: ${moment().format("DD/MM/YYYY HH:mm")}`;
      doc.text(footer, 14, footerY);
      doc.text(`Página ${doc.internal.getNumberOfPages()}`, pageWidth - 14, footerY, {
        align: "right",
      });
    },
  });

  // (Opcional) línea de “Cantidad de registros” discreta
  const finalY = doc.lastAutoTable?.finalY ? doc.lastAutoTable.finalY + 6 : y + 40;
  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  doc.setTextColor(...COLORS.grayText);

  doc.save(`${sectionTitle}-Liquidacion-${idLiquidacion}.pdf`);
}

function buildTable(section, rows) {
  // === EXTRAS / OTROS SERVICIOS (como imagen: Detalle | Monto | Fecha) ===
  if (section === "extras") {
    const headers = ["Detalle", "Monto", "Fecha"];

    const body = rows.map((g) => {
      const detalle = safe(g.gasto);
      // si querés incluir factura sin agregar columna:
      const detFinal = g.factura ? `${detalle} (Fact: ${safe(g.factura)})` : detalle;

      return [
        detFinal,
        toNumber(g.monto).toLocaleString("es"),
        safe(g.fecha),
      ];
    });

    const totalValue = rows.reduce((acc, r) => acc + toNumber(r.monto), 0);

    // Subtotal como en la imagen
    const foot = ["Subtotal", totalValue.toLocaleString("es"), ""];

    return {
      headers,
      body,
      foot,
      columnStyles: {
        0: { cellWidth: 110 },
        1: { halign: "right", cellWidth: 35 },
        2: { halign: "center", cellWidth: 30 },
      },
    };
  }

  // === ASISTENCIAS (Detalle | Fecha | Tarifa | Asignado) ===
  if (section === "asistencias") {
    const headers = ["Detalle", "Fecha", "Tarifa", "Asignado"];

    const body = rows.map((a) => [
      safe(a.procedimiento),
      safe(a.fecha),
      toNumber(a.tarifa || a.honorario).toLocaleString("es"),
      safe(a.usuario),
    ]);

    const totalValue = rows.reduce(
      (acc, r) => acc + toNumber(r.tarifa || r.honorario),
      0
    );

    // Subtotal
    const foot = ["Subtotal", "", totalValue.toLocaleString("es"), ""];

    return {
      headers,
      body,
      foot,
      columnStyles: {
        0: { cellWidth: 80 },
        1: { halign: "center", cellWidth: 30 },
        2: { halign: "right", cellWidth: 30 },
        3: { cellWidth: 50 },
      },
    };
  }

  // === MARCACIONES (como imagen: Fecha | Hora Entrada | Hora Salida | Horas | Tarifa | Enfermero) ===
  const headers = ["Fecha", "Hora Entrada", "Hora Salida", "Horas", "Tarifa", "Enfermero"];

  const body = rows.map((m) => {
    const horas = toNumber(m.horascalculadas) + toNumber(m.horashoraextra);
    const monto = toNumber(m.monto || m.total || 0);

    return [
      safe(m.Fecha),
      safe(m.HoraEntrada),
      safe(m.HoraSalida || m.horasalida || ""),
      Number(horas).toFixed(2),
      monto.toLocaleString("es"),
      safe(m.Usuario),
    ];
  });

  const totalValue = rows.reduce((acc, r) => acc + toNumber(r.monto || r.total || 0), 0);

  // Subtotal
  const foot = ["Subtotal", "", "", "", totalValue.toLocaleString("es"), ""];

  return {
    headers,
    body,
    foot,
    columnStyles: {
      0: { halign: "center", cellWidth: 25 },
      1: { halign: "center", cellWidth: 28 },
      2: { halign: "center", cellWidth: 28 },
      3: { halign: "right", cellWidth: 16 },
      4: { halign: "right", cellWidth: 25 },
      5: { cellWidth: 40 },
    },
  };
}

function safe(v) {
  return v === null || v === undefined ? "" : String(v);
}

function toNumber(v) {
  if (v === null || v === undefined) return 0;
  if (typeof v === "number") return v;

  // soporta: "1.234.567" / "1,234,567" / "1234567" / "-3.283.035"
  const s = String(v)
    .trim()
    .replace(/\s/g, "")
    .replace(/\./g, "")
    .replace(/,/g, ".");
  const n = Number(s);
  return Number.isFinite(n) ? n : 0;
}
